import React, { useState, useEffect, useCallback } from 'react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { io, Socket } from 'socket.io-client';
import { 
  ScaleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ClockIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';

// Calibration states for each scale
enum CalibrationState {
  NOT_CALIBRATED = 'NOT_CALIBRATED',
  ZERO_SET = 'ZERO_SET',
  CALIBRATED = 'CALIBRATED',
  ERROR = 'ERROR',
  EXPIRED = 'EXPIRED'
}

interface ScaleCalibration {
  stationId: string;
  stationNumber: number;
  employeeName?: string;
  currentWeight: number;
  adcValue: number;
  zeroAdc?: number;
  spanAdc?: number;
  calibrationWeight?: number;
  calibrationFactor?: number;
  state: CalibrationState;
  lastCalibrated?: Date;
  buttonPressed?: boolean;
}

interface Line {
  id: string;
  name: string;
  hardwareId: number;
}

const CalibrationPage: React.FC = () => {
  const { apiUrl } = useDatabaseContext();
  const { weightUnit } = usePreferences();
  const [socket, setSocket] = useState<Socket | null>(null);
  
  const [lines, setLines] = useState<Line[]>([]);
  const [selectedLineId, setSelectedLineId] = useState<string>('');
  const [calibrationInterval, setCalibrationInterval] = useState<number>(24);
  // Track calibration state per line
  const [calibratingLines, setCalibratingLines] = useState<Map<string, boolean>>(new Map());
  const [calibrationModes, setCalibrationModes] = useState<Map<string, 'idle' | 'batch' | 'individual'>>(new Map());
  const [activeTab, setActiveTab] = useState<'calibrate' | 'history'>('calibrate');
  const [calibrationHistory, setCalibrationHistory] = useState<any[]>([]);
  
  // Helper functions for per-line calibration state
  const isCalibrating = calibratingLines.get(selectedLineId) || false;
  const calibrationMode = calibrationModes.get(selectedLineId) || 'idle';
  
  const setIsCalibrating = (value: boolean) => {
    setCalibratingLines(prev => new Map(prev).set(selectedLineId, value));
  };
  
  const setCalibrationMode = (mode: 'idle' | 'batch' | 'individual') => {
    setCalibrationModes(prev => new Map(prev).set(selectedLineId, mode));
  };
  
  // Calibration data for each station
  const [infeedCalibration, setInfeedCalibration] = useState<ScaleCalibration | null>(null);
  const [outfeedCalibrations, setOutfeedCalibrations] = useState<ScaleCalibration[]>([]);
  
  // Standard calibration weight (configurable)
  const [calibrationWeightLbs, setCalibrationWeightLbs] = useState(25);

  // Fetch lines on mount and check for active calibration sessions
  useEffect(() => {
    const fetchLines = async () => {
      try {
        const response = await fetch(`${apiUrl}/lines`);
        if (response.ok) {
          const data = await response.json();
          const sortedLines = (data.data || []).sort((a: Line, b: Line) => {
            const aNum = a.hardwareId ?? 999;
            const bNum = b.hardwareId ?? 999;
            return aNum - bNum;
          });
          setLines(sortedLines);
          if (sortedLines.length > 0 && !selectedLineId) {
            setSelectedLineId(sortedLines[0].id);
          }
          
          // Check all lines for active calibration sessions
          for (const line of sortedLines) {
            const sessionResponse = await fetch(`${apiUrl}/calibration/session/${line.id}`);
            if (sessionResponse.ok) {
              const sessionResult = await sessionResponse.json();
              if (sessionResult.data && sessionResult.data.mode === 'CALIBRATING') {
                setCalibratingLines(prev => new Map(prev).set(line.id, true));
                setCalibrationModes(prev => new Map(prev).set(line.id, 'batch'));
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching lines:', error);
      }
    };
    fetchLines();
  }, [apiUrl, selectedLineId]);

  // Fetch calibration interval from preferences
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await fetch(`${apiUrl}/system-preferences`);
        if (response.ok) {
          const data = await response.json();
          if (data.calibration_interval_hours) {
            setCalibrationInterval(data.calibration_interval_hours);
          }
        }
      } catch (error) {
        console.error('Error fetching preferences:', error);
      }
    };
    fetchPreferences();
  }, [apiUrl]);

  // Setup WebSocket connection for real-time updates
  useEffect(() => {
    const newSocket = io(apiUrl.replace('/api', ''), {
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    newSocket.on('connect', () => {
      console.log('CalibrationPage connected to WebSocket');
    });

    // Listen for calibration state changes
    newSocket.on('calibration-state-change', (data: any) => {
      console.log('Calibration state change:', data);
      const lineId = data.lineId;
      
      // Update calibration state for the specific line
      if (data.event === 'calibration_started') {
        setCalibratingLines(prev => new Map(prev).set(lineId, true));
        setCalibrationModes(prev => new Map(prev).set(lineId, 'batch'));
        if (lineId === selectedLineId) {
          updateAllStationsState(CalibrationState.NOT_CALIBRATED);
        }
      } else if (data.event === 'calibration_ended') {
        setCalibratingLines(prev => new Map(prev).set(lineId, false));
        setCalibrationModes(prev => new Map(prev).set(lineId, 'idle'));
      } else if ((data.event === 'zero_set' || data.event === 'calibration_complete') && lineId === selectedLineId) {
        // Refresh the session to get updated states for the selected line
        checkActiveCalibrationSession(selectedLineId);
      }
    });

    // Listen for hardware button presses during calibration
    newSocket.on('hardware-button-press', (data: any) => {
      if (isCalibrating) {
        handleHardwareButtonPress(data.stationId);
      }
    });

    // Listen for weight updates
    newSocket.on('weight-update', (data: any) => {
      updateStationWeight(data.stationId, data.weight, data.adcValue);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [apiUrl, isCalibrating, selectedLineId]);

  // Load calibration data for selected line and check for active session
  useEffect(() => {
    if (selectedLineId) {
      setTimeout(() => {
        console.log('Selected line ID:', selectedLineId);
        loadCalibrationData(selectedLineId);
        checkActiveCalibrationSession(selectedLineId);
        loadLatestCalibrations(selectedLineId);
        if (activeTab === 'history') {
          loadCalibrationHistory(selectedLineId);
        }
      }, 100);
    }
  }, [selectedLineId, activeTab]);

  // Poll for calibration updates when in calibration mode
  useEffect(() => {
    if (!isCalibrating || !selectedLineId) return;

    const pollInterval = setInterval(() => {
      checkActiveCalibrationSession(selectedLineId);
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [isCalibrating, selectedLineId]);

  const loadCalibrationData = async (lineId: string) => {
    // First, get the actual hardware configuration for this line
    try {
      const configResponse = await fetch(`${apiUrl}/lines/${lineId}/hardware-config`);
      if (configResponse.ok) {
        const configResult = await configResponse.json();
        const config = configResult.data;
        
        // Only initialize infeed if it exists
        if (config.hasInfeed) {
          setInfeedCalibration({
            stationId: 'infeed',
            stationNumber: 0,
            state: CalibrationState.NOT_CALIBRATED,
            currentWeight: 0,
            adcValue: 8388608,
            zeroAdc: undefined,
            spanAdc: undefined,
            calibrationWeight: undefined,
            calibrationFactor: undefined,
            lastCalibrated: undefined
          });
        } else {
          setInfeedCalibration(null);
        }
        
        // Only initialize stations that actually exist
        const outfeedStations = [];
        for (const stationNum of config.outfeedStations) {
          outfeedStations.push({
            stationId: `station_${stationNum}`,
            stationNumber: stationNum,
            state: CalibrationState.NOT_CALIBRATED,
            currentWeight: 0,
            adcValue: 8388608,
            zeroAdc: undefined,
            spanAdc: undefined,
            calibrationWeight: undefined,
            calibrationFactor: undefined,
            lastCalibrated: undefined
          });
        }
        setOutfeedCalibrations(outfeedStations);
      }
    } catch (error) {
      console.error('Error fetching hardware configuration:', error);
      // Fall back to defaults if can't get config
      setInfeedCalibration({
        stationId: 'infeed',
        stationNumber: 0,
        state: CalibrationState.NOT_CALIBRATED,
        currentWeight: 0,
        adcValue: 8388608,
        zeroAdc: undefined,
        spanAdc: undefined,
        calibrationWeight: undefined,
        calibrationFactor: undefined,
        lastCalibrated: undefined
      });
      setOutfeedCalibrations([]);
    }
  };
  
  const loadLatestCalibrations = async (lineId: string) => {
    try {
      const response = await fetch(`${apiUrl}/calibration/latest/${lineId}`);
      if (response.ok) {
        const result = await response.json();
        if (result.data && result.data.length > 0) {
          // Update calibration data for each scale
          result.data.forEach((cal: any) => {
            if (cal.scale_type === 'INFEED') {
              setInfeedCalibration(prev => prev ? { 
                ...prev, 
                lastCalibrated: cal.calibrated_at,
                zeroAdc: parseInt(cal.zero_adc),
                spanAdc: parseInt(cal.span_adc),
                calibrationWeight: parseFloat(cal.span_weight),
                calibrationFactor: parseFloat(cal.calibration_factor),
                state: CalibrationState.CALIBRATED
              } : null);
            } else if (cal.scale_type === 'OUTFEED' && cal.station_number) {
              setOutfeedCalibrations(prev => 
                prev.map(s => s.stationNumber === cal.station_number ? {
                  ...s,
                  lastCalibrated: cal.calibrated_at,
                  zeroAdc: parseInt(cal.zero_adc),
                  spanAdc: parseInt(cal.span_adc),
                  calibrationWeight: parseFloat(cal.span_weight),
                  calibrationFactor: parseFloat(cal.calibration_factor),
                  state: CalibrationState.CALIBRATED
                } : s)
              );
            }
          });
        }
      }
    } catch (error) {
      console.error('Error loading latest calibrations:', error);
    }
  };
  
  const loadCalibrationHistory = async (lineId: string) => {
    try {
      const response = await fetch(`${apiUrl}/calibration/history/${lineId}?limit=100`);
      if (response.ok) {
        const result = await response.json();
        setCalibrationHistory(result.data || []);
      }
    } catch (error) {
      console.error('Error loading calibration history:', error);
    }
  };

  const checkActiveCalibrationSession = async (lineId: string) => {
    try {
      // First get the hardware configuration
      const configResponse = await fetch(`${apiUrl}/lines/${lineId}/hardware-config`);
      let hardwareConfig = { hasInfeed: true, outfeedStations: [] };
      if (configResponse.ok) {
        const configResult = await configResponse.json();
        hardwareConfig = configResult.data;
      }
      
      const response = await fetch(`${apiUrl}/calibration/session/${lineId}`);
      if (response.ok) {
        const result = await response.json();
        if (result.data && result.data.mode === 'CALIBRATING') {
          // Restore calibration state from active session for this line
          setCalibratingLines(prev => new Map(prev).set(lineId, true));
          setCalibrationModes(prev => new Map(prev).set(lineId, 'batch'));
          
          // Restore station states from session data
          const calibrationData = result.data.calibration_data || {};
          const stationsCalibrated = result.data.stations_calibrated || [];
          
          // Update infeed state only if hardware exists
          if (hardwareConfig.hasInfeed) {
            if (calibrationData.infeed) {
              const infeedState = calibrationData.infeed.calibrated 
                ? CalibrationState.CALIBRATED 
                : calibrationData.infeed.zeroSet 
                ? CalibrationState.ZERO_SET 
                : CalibrationState.NOT_CALIBRATED;
              
              setInfeedCalibration(prev => ({
                ...prev,
                state: infeedState,
                zeroAdc: calibrationData.infeed.zeroAdc || prev.zeroAdc,
                spanAdc: calibrationData.infeed.spanAdc || prev.spanAdc,
                calibrationWeight: calibrationData.infeed.calibrationWeight || prev.calibrationWeight
              }));
            } else {
              setInfeedCalibration(prev => ({
                ...prev,
                state: CalibrationState.NOT_CALIBRATED
              }));
            }
          } else {
            setInfeedCalibration(null);
          }
          
          // Update only stations that actually exist
          setOutfeedCalibrations(prev => {
            const updatedOutfeed = [];
            for (const stationNum of hardwareConfig.outfeedStations) {
              const stationKey = `station_${stationNum}`;
              const stationData = calibrationData[stationKey];
              
              let state = CalibrationState.NOT_CALIBRATED;
              if (stationData) {
                state = stationData.calibrated 
                  ? CalibrationState.CALIBRATED 
                  : stationData.zeroSet 
                  ? CalibrationState.ZERO_SET 
                  : CalibrationState.NOT_CALIBRATED;
              }
              
              // Keep existing weight/ADC if available
              const existing = prev.find(s => s.stationId === stationKey);
              
              updatedOutfeed.push({
                stationId: stationKey,
                stationNumber: stationNum,
                state,
                zeroAdc: stationData?.zeroAdc || 8388608,
                spanAdc: stationData?.spanAdc || 16777215,
                calibrationWeight: stationData?.calibrationWeight || 25,
                currentWeight: existing?.currentWeight || 0,
                currentAdc: existing?.currentAdc || 8388608,
                lastCalibrated: stationData?.calibrationTime || null
              });
            }
            return updatedOutfeed;
          });
          
          console.log('Restored active calibration session:', result.data.id);
        }
      }
    } catch (error) {
      console.error('Error checking calibration session:', error);
    }
  };

  const startBatchCalibration = async () => {
    // Send command to hardware to start calibration mode
    try {
      const response = await fetch(`${apiUrl}/calibration/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          lineId: selectedLineId,
          startedBy: 'Operator'
        })
      });
      
      if (response.ok) {
        setIsCalibrating(true);
        setCalibrationMode('batch');
        // All station lights should now be flashing red/yellow
        // Update UI to show calibration in progress
        updateAllStationsState(CalibrationState.NOT_CALIBRATED);
      } else {
        console.error('Failed to start calibration');
      }
    } catch (error) {
      console.error('Error starting batch calibration:', error);
    }
  };

  const handleHardwareButtonPress = (stationId: string) => {
    // Find the station's current state
    const station = stationId === 'infeed' 
      ? infeedCalibration 
      : outfeedCalibrations.find(s => s.stationId === stationId);
    
    if (!station) return;
    
    if (station.state === CalibrationState.NOT_CALIBRATED) {
      // First press - set zero
      setZeroCalibration(stationId);
    } else if (station.state === CalibrationState.ZERO_SET) {
      // Second press - set span with calibration weight
      setSpanCalibration(stationId);
    }
  };

  const setZeroCalibration = async (stationId: string) => {
    try {
      const response = await fetch(`${apiUrl}/calibration/set-zero`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          lineId: selectedLineId,
          stationId: stationId,
          adcValue: getCurrentAdcValue(stationId)
        })
      });
      
      if (response.ok) {
        updateStationState(stationId, CalibrationState.ZERO_SET);
        // Light changes to flashing yellow
      }
    } catch (error) {
      console.error('Error setting zero calibration:', error);
      updateStationState(stationId, CalibrationState.ERROR);
    }
  };

  const setSpanCalibration = async (stationId: string) => {
    try {
      const response = await fetch(`${apiUrl}/calibration/set-span`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          lineId: selectedLineId,
          stationId: stationId,
          adcValue: getCurrentAdcValue(stationId),
          knownWeight: calibrationWeightLbs
        })
      });
      
      if (response.ok) {
        updateStationState(stationId, CalibrationState.CALIBRATED);
        // Light changes to solid green
      }
    } catch (error) {
      console.error('Error setting span calibration:', error);
      updateStationState(stationId, CalibrationState.ERROR);
    }
  };

  // Note: Calibration data is now saved automatically when each scale is calibrated
  // through the button press simulation. The history is recorded in the calibration_history table.

  // Helper functions
  const getCurrentAdcValue = (stationId: string): number => {
    const station = stationId === 'infeed' 
      ? infeedCalibration 
      : outfeedCalibrations.find(s => s.stationId === stationId);
    return station?.adcValue || 0;
  };

  const updateStationWeight = (stationId: string, weight: number, adcValue: number) => {
    if (stationId === 'infeed') {
      setInfeedCalibration(prev => prev ? { ...prev, currentWeight: weight, adcValue } : null);
    } else {
      setOutfeedCalibrations(prev => 
        prev.map(s => s.stationId === stationId ? { ...s, currentWeight: weight, adcValue } : s)
      );
    }
  };

  const updateStationState = (stationId: string, state: CalibrationState) => {
    if (stationId === 'infeed') {
      setInfeedCalibration(prev => prev ? { ...prev, state } : null);
    } else {
      setOutfeedCalibrations(prev => 
        prev.map(s => s.stationId === stationId ? { ...s, state } : s)
      );
    }
  };

  const updateAllStationsState = (state: CalibrationState) => {
    if (infeedCalibration) {
      setInfeedCalibration({ ...infeedCalibration, state });
    }
    setOutfeedCalibrations(prev => prev.map(s => ({ ...s, state })));
  };

  const getStateColor = (state: CalibrationState): string => {
    switch (state) {
      case CalibrationState.CALIBRATED:
        return 'bg-green-100 text-green-800 border-green-300';
      case CalibrationState.ZERO_SET:
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case CalibrationState.NOT_CALIBRATED:
      case CalibrationState.EXPIRED:
        return 'bg-red-100 text-red-800 border-red-300';
      case CalibrationState.ERROR:
        return 'bg-red-200 text-red-900 border-red-400';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStateIcon = (state: CalibrationState) => {
    switch (state) {
      case CalibrationState.CALIBRATED:
        return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
      case CalibrationState.ZERO_SET:
        return <ClockIcon className="w-5 h-5 text-yellow-600" />;
      case CalibrationState.NOT_CALIBRATED:
      case CalibrationState.EXPIRED:
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />;
      case CalibrationState.ERROR:
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-800" />;
      default:
        return <ScaleIcon className="w-5 h-5 text-gray-600" />;
    }
  };

  const formatWeight = (weight: number | undefined): string => {
    if (weight === undefined || weight === null) {
      return '0.00 lbs';
    }
    if (weightUnit === 'KGS') {
      return `${(weight * 0.453592).toFixed(2)} kg`;
    }
    return `${weight.toFixed(2)} lbs`;
  };

  const getCalibrationAge = (calibratedAt: Date | string | undefined): string => {
    if (!calibratedAt) return 'Never';
    
    const calibrationDate = new Date(calibratedAt);
    const now = new Date();
    const diffMs = now.getTime() - calibrationDate.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours === 0) {
      if (diffMinutes === 0) return 'Just now';
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    }
  };

  const getCalibrationHours = (calibratedAt: Date | string | undefined): number => {
    if (!calibratedAt) return 999;
    const calibrationDate = new Date(calibratedAt);
    const now = new Date();
    return (now.getTime() - calibrationDate.getTime()) / (1000 * 60 * 60);
  };

  const getCalibrationStatusStyle = (calibratedAt: Date | string | undefined): string => {
    const hours = getCalibrationHours(calibratedAt);
    if (hours < 12) return 'bg-green-50 border border-green-200 rounded-md p-3';
    if (hours < 24) return 'bg-yellow-50 border border-yellow-200 rounded-md p-3';
    return 'bg-red-50 border border-red-200 rounded-md p-3';
  };

  const getCalibrationStatusIcon = (calibratedAt: Date | string | undefined) => {
    const hours = getCalibrationHours(calibratedAt);
    if (hours < 12) return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
    if (hours < 24) return <ClockIcon className="w-5 h-5 text-yellow-600" />;
    return <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />;
  };

  const getCalibrationStatusText = (calibratedAt: Date | string | undefined): string => {
    const hours = getCalibrationHours(calibratedAt);
    if (hours < 12) return 'Calibration Valid';
    if (hours < 24) return 'Calibration Aging';
    return 'Calibration Expired';
  };

  const getCalibrationStatusTextStyle = (calibratedAt: Date | string | undefined): string => {
    const hours = getCalibrationHours(calibratedAt);
    if (hours < 12) return 'font-medium text-green-900';
    if (hours < 24) return 'font-medium text-yellow-900';
    return 'font-medium text-red-900';
  };

  const getStationCalibrationBorderStyle = (calibratedAt: Date | string | undefined): string => {
    const hours = getCalibrationHours(calibratedAt);
    if (hours < 12) return 'border-green-300';
    if (hours < 24) return 'border-yellow-300';
    return 'border-red-300';
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Scale Calibration</h1>
              <p className="text-sm text-gray-600 mt-1">
                Calibration interval: {calibrationInterval} hours | 
                Weight unit: {weightUnit}
              </p>
            </div>
            <div className="flex space-x-3">
              {!isCalibrating ? (
                <button
                  onClick={startBatchCalibration}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center space-x-2"
                >
                  <BeakerIcon className="w-5 h-5" />
                  <span>Start Line Calibration</span>
                </button>
              ) : (
                <>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    <span>Calibration data saves automatically as each scale is calibrated</span>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const response = await fetch(`${apiUrl}/calibration/end`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ 
                            lineId: selectedLineId,
                            completedBy: 'Operator'
                          })
                        });
                        
                        if (response.ok) {
                          setIsCalibrating(false);
                          setCalibrationMode('idle');
                          // Reload latest calibrations to show updated timestamps
                          loadLatestCalibrations(selectedLineId);
                        }
                      } catch (error) {
                        console.error('Error ending calibration:', error);
                      }
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center space-x-2"
                  >
                    <CheckCircleIcon className="w-5 h-5" />
                    <span>Complete Calibration</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Line Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-1 p-2">
              {lines.map(line => {
                const lineIsCalibrating = calibratingLines.get(line.id) || false;
                return (
                  <button
                    key={line.id}
                    data-testid={`line-tab-${line.id}`}
                    onClick={() => setSelectedLineId(line.id)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2 ${
                      selectedLineId === line.id
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span>{line.name}</span>
                    {lineIsCalibrating && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        selectedLineId === line.id ? 'bg-white bg-opacity-20 text-white' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        <ArrowPathIcon className="w-3 h-3 mr-1 animate-spin" />
                        Calibrating
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
        
        {/* Tab Navigation for Calibrate/History */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('calibrate')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'calibrate'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Calibration
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'history'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                History
              </button>
            </nav>
          </div>
        </div>

        {/* Calibration Tab Content */}
        {activeTab === 'calibrate' && (
          <>
        {/* Calibration Status */}
        {isCalibrating && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <ArrowPathIcon className="w-5 h-5 text-yellow-600 mr-2 animate-spin" />
              <div>
                <p className="font-medium text-yellow-800">Calibration in Progress</p>
                <p className="text-sm text-yellow-700">
                  1. Ensure scales are empty and press station button to set zero
                  2. Place {calibrationWeightLbs} lb weight and press button again to calibrate
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Infeed Scale */}
        {infeedCalibration && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Infeed Scale</h2>
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-600">Current Weight</p>
                <p className="text-xl font-bold">{formatWeight(infeedCalibration.currentWeight)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Current ADC</p>
                <p className="text-xl font-mono">{infeedCalibration.adcValue || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStateColor(infeedCalibration.state)}`}>
                  {getStateIcon(infeedCalibration.state)}
                  <span className="ml-2">{infeedCalibration.state.replace('_', ' ')}</span>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Last Calibrated</p>
                <p className="text-sm">
                  {infeedCalibration.lastCalibrated 
                    ? new Date(infeedCalibration.lastCalibrated).toLocaleString()
                    : 'Never'}
                </p>
              </div>
            </div>
            {/* Calibration Details */}
            {infeedCalibration.zeroAdc && (
              <div className="border-t pt-4">
                <div className="grid grid-cols-4 gap-4 mb-3">
                  <div>
                    <p className="text-sm text-gray-600">Zero Point</p>
                    <p className="text-sm font-mono">{infeedCalibration.zeroAdc}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Span Point</p>
                    <p className="text-sm font-mono">{infeedCalibration.spanAdc || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Test Weight</p>
                    <p className="text-sm">{infeedCalibration.calibrationWeight ? `${infeedCalibration.calibrationWeight} lbs` : '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Scale Sensitivity</p>
                    <p className="text-sm font-medium">
                      {infeedCalibration.calibrationFactor 
                        ? `${Math.round(1 / parseFloat(infeedCalibration.calibrationFactor.toString())).toLocaleString()} counts/lb`
                        : '-'}
                    </p>
                  </div>
                </div>
                {/* User-friendly calibration status */}
                {infeedCalibration.lastCalibrated && (
                  <div className={getCalibrationStatusStyle(infeedCalibration.lastCalibrated)}>
                    <div className="flex items-center space-x-2">
                      {getCalibrationStatusIcon(infeedCalibration.lastCalibrated)}
                      <div className="text-sm">
                        <span className={getCalibrationStatusTextStyle(infeedCalibration.lastCalibrated)}>
                          {getCalibrationStatusText(infeedCalibration.lastCalibrated)}
                        </span>
                        <span className="text-gray-700 ml-2">
                          • Range: 0-{infeedCalibration.calibrationWeight || 25} lbs
                        </span>
                        <span className="text-gray-700 ml-2">
                          • Age: {getCalibrationAge(infeedCalibration.lastCalibrated)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Outfeed Scales */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Outfeed Scales</h2>
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column - Stations 1-5 */}
            <div className="space-y-4">
              {outfeedCalibrations.slice(0, 5).map(station => (
                <div key={station.stationId} data-testid={`outfeed-scale-${station.stationNumber}`} className={`border rounded-lg p-4 ${station.lastCalibrated ? getStationCalibrationBorderStyle(station.lastCalibrated) : ''}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-start space-x-2">
                      {/* Calibration status indicator */}
                      {station.lastCalibrated && (
                        <div className="mt-1" title={`Calibration ${getCalibrationStatusText(station.lastCalibrated)}`}>
                          {getCalibrationStatusIcon(station.lastCalibrated)}
                        </div>
                      )}
                      <div>
                        <h3 className="font-medium">Station {station.stationNumber}</h3>
                        {station.employeeName && (
                          <p className="text-sm text-gray-600">{station.employeeName}</p>
                        )}
                      </div>
                    </div>
                    <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStateColor(station.state)}`}>
                      <span>{station.state.replace('_', ' ')}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Weight:</span>
                      <span className="ml-2 font-medium">{formatWeight(station.currentWeight)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">ADC:</span>
                      <span className="ml-2 font-mono">{station.adcValue || 0}</span>
                    </div>
                  </div>
                  {station.zeroAdc && (
                    <div className="mt-2 pt-2 border-t space-y-1">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-gray-600">Zero:</span>
                          <span className="ml-1 font-mono">{station.zeroAdc}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Span:</span>
                          <span className="ml-1 font-mono">{station.spanAdc || '-'}</span>
                        </div>
                      </div>
                      {station.calibrationFactor && (
                        <div className="text-xs">
                          <span className="text-gray-600">Sensitivity:</span>
                          <span className="ml-1 font-medium">
                            {Math.round(1 / station.calibrationFactor).toLocaleString()} counts/lb
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {station.lastCalibrated && (
                    <div className="mt-1 space-y-1">
                      <div className="text-xs text-gray-600">
                        <span className="font-medium">Calibration Age:</span> {getCalibrationAge(station.lastCalibrated)}
                      </div>
                      <div className="text-xs text-gray-600">
                        <span className="font-medium">Range:</span> 0-{station.calibrationWeight || 25} lbs
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Right Column - Stations 6-10 */}
            <div className="space-y-4">
              {outfeedCalibrations.slice(5, 10).map(station => (
                <div key={station.stationId} data-testid={`outfeed-scale-${station.stationNumber}`} className={`border rounded-lg p-4 ${station.lastCalibrated ? getStationCalibrationBorderStyle(station.lastCalibrated) : ''}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-start space-x-2">
                      {/* Calibration status indicator */}
                      {station.lastCalibrated && (
                        <div className="mt-1" title={`Calibration ${getCalibrationStatusText(station.lastCalibrated)}`}>
                          {getCalibrationStatusIcon(station.lastCalibrated)}
                        </div>
                      )}
                      <div>
                        <h3 className="font-medium">Station {station.stationNumber}</h3>
                        {station.employeeName && (
                          <p className="text-sm text-gray-600">{station.employeeName}</p>
                        )}
                      </div>
                    </div>
                    <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStateColor(station.state)}`}>
                      <span>{station.state.replace('_', ' ')}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Weight:</span>
                      <span className="ml-2 font-medium">{formatWeight(station.currentWeight)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">ADC:</span>
                      <span className="ml-2 font-mono">{station.adcValue || 0}</span>
                    </div>
                  </div>
                  {station.zeroAdc && (
                    <div className="mt-2 pt-2 border-t space-y-1">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-gray-600">Zero:</span>
                          <span className="ml-1 font-mono">{station.zeroAdc}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Span:</span>
                          <span className="ml-1 font-mono">{station.spanAdc || '-'}</span>
                        </div>
                      </div>
                      {station.calibrationFactor && (
                        <div className="text-xs">
                          <span className="text-gray-600">Sensitivity:</span>
                          <span className="ml-1 font-medium">
                            {Math.round(1 / station.calibrationFactor).toLocaleString()} counts/lb
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {station.lastCalibrated && (
                    <div className="mt-1 space-y-1">
                      <div className="text-xs text-gray-600">
                        <span className="font-medium">Calibration Age:</span> {getCalibrationAge(station.lastCalibrated)}
                      </div>
                      <div className="text-xs text-gray-600">
                        <span className="font-medium">Range:</span> 0-{station.calibrationWeight || 25} lbs
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Calibration Settings */}
        {isCalibrating && (
          <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Calibration Settings</h3>
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Calibration Weight ({weightUnit === 'KGS' ? 'kg' : 'lbs'})
                </label>
                <input
                  type="number"
                  value={calibrationWeightLbs}
                  onChange={(e) => setCalibrationWeightLbs(parseFloat(e.target.value) || 25)}
                  className="mt-1 w-32 px-3 py-2 border border-gray-300 rounded-md"
                  min="1"
                  max="100"
                  step="0.1"
                />
              </div>
            </div>
          </div>
        )}
          </>
        )}
        
        {/* History Tab Content */}
        {activeTab === 'history' && selectedLineId && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Calibration History</h2>
            {calibrationHistory.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date/Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Scale
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Zero ADC
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Span ADC
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Span Weight
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Calibrated By
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {calibrationHistory.map((record) => (
                      <tr key={record.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(record.calibrated_at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.scale_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.calibration_type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                          {record.zero_adc || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                          {record.span_adc || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.span_weight ? `${record.span_weight} lbs` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.calibrated_by}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            record.calibration_status === 'SUCCESS' 
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {record.calibration_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No calibration history available for this line.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CalibrationPage;